import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { ErrorToast } from "utils/toast";
import Icon from "Components/Icons/Icon";

export interface FilterParams {
  startDate?: string | null;
  endDate?: string | null;
  statuses?: string[];
}

interface ClientDetailsFilterProps {
  show: boolean;
  selectedFilter: FilterParams | null;
  onCloseClick: () => void;
  onApplyClick: (e: FilterParams) => void;
  onCanCelClick: () => void;
}

const ClientDetailsFilter: React.FC<ClientDetailsFilterProps> = ({
  show,
  onCloseClick,
  onApplyClick,
  onCanCelClick,
  selectedFilter,
}) => {
  // -----------------------------
  // FILTER STATES
  // -----------------------------
  const [startDate, setStartDate] = useState<string | null>(
    selectedFilter?.startDate || null,
  );
  const [endDate, setEndDate] = useState<string | null>(
    selectedFilter?.endDate || null,
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    selectedFilter?.statuses || [],
  );

  const statuses = [
    { displayName: "Paid", value: "paid" },
    { displayName: "Overdue", value: "overdue" },
    { displayName: "Sent", value: "sent" },
    { displayName: "Rejected", value: "rejected" },
    { displayName: "Due", value: "due" },
    { displayName: "Draft", value: "draft" },
    { displayName: "Approved", value: "approved" },
    { displayName: "Completed", value: "completed" },
    { displayName: "Cancelled", value: "cancelled" },
  ];

  // -----------------------------
  // HANDLE CHECKBOX CHANGE
  // -----------------------------
  const handleStatusChange = (value: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  // -----------------------------
  // APPLY FILTER
  // -----------------------------
  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate && !endDate && !selectedStatuses.length) {
      ErrorToast("Please select at least one filter option.");
      return;
    }
    console.log("applyFilters", { startDate, endDate, selectedStatuses });

    onApplyClick({
      startDate,
      endDate,
      statuses: selectedStatuses,
    });
  };

  // -----------------------------
  // CLEAR FILTER
  // -----------------------------
  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedStatuses([]);
    onCanCelClick();
  };

  useEffect(() => {
    console.log("selectedFilter changed", selectedFilter);

    if (selectedFilter) {
      setStartDate(selectedFilter?.startDate || "");
      setEndDate(selectedFilter.endDate || "");
      setSelectedStatuses(selectedFilter?.statuses || []);
    }
  }, [selectedFilter]);

  const handleCalendarIconClick = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  return (
    <Offcanvas direction="end" isOpen={show} toggle={onCloseClick}>
      <OffcanvasHeader className="bg-light" toggle={onCloseClick}>
        Filters
      </OffcanvasHeader>

      <form
        className="d-flex flex-column justify-content-end h-100"
        onSubmit={applyFilters}
      >
        <OffcanvasBody>
          {/* DATE FILTER */}
          <Label className="form-label text-muted text-uppercase fw-base mb-3">
            DATE RANGE
          </Label>

          <Row className="g-2 mb-4">
            <Col lg={6}>
              <Label className="form-label text-uppercase fw-base mb-2">
                Start Date
              </Label>
              <div className="position-relative">
                <Flatpickr
                  className="form-control"
                  placeholder="Start Date"
                  value={startDate ? new Date(startDate) : undefined}
                  options={{
                    disableMobile: true,
                    // static: true,
                    maxDate: endDate ? new Date(endDate) : undefined,
                    onChange: (selectedDates, dateStr) => {
                      setStartDate(dateStr || null);
                    },
                    position: "below",
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleCalendarIconClick("start-date-picker")}
                />
              </div>
            </Col>

            <Col lg={6}>
              <Label className="form-label text-uppercase fw-base mb-2">
                End Date
              </Label>
              <div className="position-relative">
                <Flatpickr
                  className="form-control"
                  placeholder="End Date"
                  value={endDate ? new Date(endDate) : undefined}
                  options={{
                    disableMobile: true,
                    // static: true,
                    minDate: startDate ? new Date(startDate) : undefined,
                    onChange: (selectedDates, dateStr) => {
                      setEndDate(dateStr || null);
                    },
                    position: "below",
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleCalendarIconClick("end-date-picker")}
                />
              </div>
            </Col>
          </Row>

          {/* STATUS FILTER */}
          <Label className="form-label text-muted text-uppercase fw-base mb-3">
            Status
          </Label>

          <Row className="g-2">
            {statuses.map((item, i) => (
              <Col lg={6} key={i}>
                <div className="form-check">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedStatuses.includes(item.value)}
                    onChange={() => handleStatusChange(item.value)}
                    id={`status-${i}`}
                  />
                  <Label className="form-check-label" htmlFor={`status-${i}`}>
                    {item.displayName}
                  </Label>
                </div>
              </Col>
            ))}
          </Row>
        </OffcanvasBody>

        {/* FOOTER BUTTONS */}
        <div className="offcanvas-footer border-top p-3 hstack gap-2">
          <button
            type="button"
            className="btn btn-light w-100"
            onClick={clearFilters}
          >
            Clear
          </button>

          <button type="submit" className="btn btn-primary w-100">
            Apply
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default ClientDetailsFilter;
